import { motion } from "framer-motion";

import SectionHeading from "@/components/SectionHeading";
import { iconRegistry } from "@/content/iconRegistry";

function gridClasses(variant) {
  if (variant === "format") {
    return "grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4";
  }
  if (variant === "audience" || variant === "examples") {
    return "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6";
  }
  return "grid grid-cols-1 md:grid-cols-2 gap-6";
}

function cardClasses(variant) {
  if (variant === "format") {
    return "bg-card rounded-xl p-4 shadow-soft text-center border border-border";
  }
  if (variant === "curriculum" || variant === "examples") {
    return "bg-card rounded-2xl p-6 shadow-soft card-hover border border-border";
  }
  return "bg-card rounded-2xl p-6 shadow-soft card-hover border border-border";
}

export default function IconCardGridSection({ content, variant = "standard", dataTestId, className = "", sectionId }) {
  return (
    <section className={`py-16 md:py-24 ${className}`.trim()} data-testid={dataTestId} id={sectionId}>
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="mb-12"
        >
          <SectionHeading title={content.title} description={content.description} />
        </motion.div>

        <div className={gridClasses(variant)}>
          {content.cards.map((card, index) => {
            const Icon = iconRegistry[card.icon];

            if (variant === "format") {
              return (
                <motion.div
                  key={card.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.05 }}
                  className={cardClasses(variant)}
                >
                  <Icon className="w-6 h-6 text-highlight mx-auto mb-2" />
                  <p className="font-body text-sm text-foreground/90">{card.title}</p>
                </motion.div>
              );
            }

            if (variant === "examples") {
              return (
                <motion.div
                  key={card.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className={cardClasses(variant)}
              >
                  <div className="w-full h-32 bg-gradient-to-br from-secondary/80 to-background rounded-xl mb-4 flex items-center justify-center border border-white/10">
                    <Icon className="w-10 h-10 text-highlight/80" />
                  </div>
                  <h3 className="font-sans text-lg font-semibold text-foreground mb-1">{card.title}</h3>
                  <p className="font-body text-sm text-muted-foreground">{card.description}</p>
                </motion.div>
              );
            }

            if (variant === "curriculum") {
              return (
                <motion.div
                  key={card.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className={cardClasses(variant)}
                >
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-secondary/36 flex items-center justify-center flex-shrink-0 border border-secondary/45">
                      <Icon className="w-6 h-6 text-highlight" />
                    </div>
                    <div>
                      {card.kicker ? (
                        <span className="inline-block font-body text-xs font-medium text-highlight mb-2">{card.kicker}</span>
                      ) : null}
                      <h3 className="font-sans text-xl font-semibold text-foreground mb-2">{card.title}</h3>
                      <p className="font-body text-muted-foreground leading-relaxed">{card.description}</p>
                    </div>
                  </div>
                </motion.div>
              );
            }

            return (
              <motion.div
                key={card.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className={cardClasses(variant)}
              >
                <div className="w-12 h-12 rounded-xl bg-secondary/36 flex items-center justify-center mb-4 border border-secondary/45">
                  <Icon className="w-6 h-6 text-highlight" />
                </div>
                <h3 className="font-sans text-lg font-semibold text-foreground mb-2">{card.title}</h3>
                <p className="font-body text-muted-foreground text-sm leading-relaxed">{card.description}</p>
              </motion.div>
            );
          })}
        </div>

        {content.accentLine ? (
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="text-center font-sans text-xl font-medium text-foreground/90 mt-8"
          >
            {content.accentLine}
          </motion.p>
        ) : null}
      </div>
    </section>
  );
}
